import { useState } from "react";
import axios from "axios";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  { value: "live-in", label: "Live-In Care" },
  { value: "overnight", label: "Overnight Care" },
  { value: "visiting", label: "Visiting / Hourly Care" },
  { value: "general", label: "I'm not sure yet" },
];

const EnquiryForm = ({ defaultService = "live-in", sourcePage = "" }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    postcode: "",
    email: "",
    service: defaultService,
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setStatus({ state: "error", error: "Please share your name and phone." });
      return;
    }
    setStatus({ state: "loading", error: "" });
    try {
      const payload = { ...form, source_page: sourcePage || window.location.pathname };
      // strip empty strings to keep payload clean
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await axios.post(`${API}/enquiry`, payload);
      // GTM/GA4 conversion event
      if (typeof window !== "undefined") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "enquiry_submitted",
          form_source: sourcePage || window.location.pathname,
          service: form.service || "unknown",
        });
      }
      setStatus({ state: "success", error: "" });
      setForm({ name: "", phone: "", postcode: "", email: "", service: defaultService, message: "" });
    } catch (err) {
      setStatus({
        state: "error",
        error:
          err?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong. Please try again or call us.",
      });
    }
  };

  if (status.state === "success") {
    return (
      <div
        data-testid="enquiry-success"
        className="rounded-2xl bg-[var(--ss-cream-soft)] border border-[var(--ss-gold)]/40 p-8 text-center"
      >
        <div className="w-14 h-14 mx-auto rounded-full bg-[var(--ss-teal-700)] text-[var(--ss-cream)] flex items-center justify-center text-2xl">
          <i className="fa-solid fa-check"></i>
        </div>
        <h3 className="font-serif text-3xl text-[var(--ss-teal-800)] mt-4">
          Thank you — we'll be in touch.
        </h3>
        <p className="text-[var(--ss-ink-soft)] mt-2">
          A member of our family team will call you back shortly. If it's urgent,
          please call us on{" "}
          <a href={SITE.phoneHref} className="text-[var(--ss-teal-700)] font-semibold underline">
            {SITE.phone}
          </a>
          .
        </p>
        <button
          data-testid="enquiry-send-another"
          className="btn btn-outline-dark mt-6"
          onClick={() => setStatus({ state: "idle", error: "" })}
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="enquiry-form"
      onSubmit={submit}
      className="grid gap-4"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-[var(--ss-ink-soft)] mb-1.5">
            Your name *
          </label>
          <input
            data-testid="enquiry-name"
            className="input"
            placeholder="e.g. Rebecca Hughes"
            value={form.name}
            onChange={update("name")}
            required
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-[var(--ss-ink-soft)] mb-1.5">
            Phone *
          </label>
          <input
            data-testid="enquiry-phone"
            className="input"
            placeholder="07700 900 000"
            value={form.phone}
            onChange={update("phone")}
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.18em] text-[var(--ss-ink-soft)] mb-1.5">
          Email
        </label>
        <input
          data-testid="enquiry-email"
          type="email"
          className="input"
          placeholder="you@example.com"
          value={form.email}
          onChange={update("email")}
          autoComplete="email"
          inputMode="email"
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-[var(--ss-ink-soft)] mb-1.5">
            Postcode
          </label>
          <input
            data-testid="enquiry-postcode"
            className="input"
            placeholder="CM16"
            value={form.postcode}
            onChange={update("postcode")}
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-[var(--ss-ink-soft)] mb-1.5">
            Type of care
          </label>
          <select
            data-testid="enquiry-service"
            className="input appearance-none cursor-pointer"
            value={form.service}
            onChange={update("service")}
          >
            {services.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs uppercase tracking-[0.18em] text-[var(--ss-ink-soft)] mb-1.5">
          Tell us briefly about your situation
        </label>
        <textarea
          data-testid="enquiry-message"
          className="input min-h-[110px] resize-y"
          placeholder="e.g. Mum is 82 and lives alone since Dad passed. We're worried about overnights..."
          value={form.message}
          onChange={update("message")}
        />
      </div>

      {status.state === "error" && (
        <div
          data-testid="enquiry-error"
          className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm"
        >
          <i className="fa-solid fa-circle-exclamation mr-2"></i>
          {status.error}
        </div>
      )}

      <button
        data-testid="enquiry-submit"
        type="submit"
        disabled={status.state === "loading"}
        className="btn btn-primary w-full sm:w-auto sm:self-start mt-2"
      >
        {status.state === "loading" ? (
          <>
            <i className="fa-solid fa-circle-notch fa-spin"></i>
            Sending...
          </>
        ) : (
          <>
            <i className="fa-regular fa-paper-plane"></i>
            Request a free callback
          </>
        )}
      </button>
      <p className="text-xs text-[var(--ss-ink-soft)] mt-1">
        We'll only use your details to respond to this enquiry. No spam, ever.
      </p>
    </form>
  );
};

export default EnquiryForm;
